import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { makeId, idPrefixes } from '@dashboard/services/uuid'
import { campaignSchema, type Campaign } from '@dashboard/types/config'
import { useDomainNameStore } from '@dashboard/modules/settings/stores/domainNameStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { klona } from 'klona'
import { storeToRefs } from 'pinia'

export const useCampaignStore = defineStore('campaign', () => {
  const configStore = useConfigStore()
  const { config } = storeToRefs(configStore)

  const campaigns = computed(() => [...(config.value?.campaigns || [])].sort((a, b) => a.name.localeCompare(b.name)))
  const getById = (id: string) => campaigns.value.find((campaign) => campaign.id === id)

  const create = (campaign: Partial<Campaign>) => {
    if (!config.value) {
      return
    }

    const newCampaign = campaignSchema.parse({
      id: makeId(idPrefixes.CAMPAIGN),
      name: `New Campaign ${campaigns.value.length + 1}`,
      isActive: true,
      dimension_id: null,
      recipe_code: null,
      goal_ids: [],
      outcome: 'any_goal',
      condition: true,
      actions: [],
      ...campaign
    }) as Campaign

    // Extract domain from base_url and add to domainNames if not exists. Hosted hosts are project domains by
    // derivation, so a campaign on a hosted page never writes one into the config.
    if (campaign.base_url) {
      const domainNameStore = useDomainNameStore()
      const url = new URL(campaign.base_url)
      const hostname = url.hostname

      if (!domainNameStore.domains.includes(hostname) && !configStore.currentTeam?.hostedHosts?.includes(hostname)) {
        domainNameStore.create({ name: hostname })
      }
    }

    config.value.campaigns.push(newCampaign)

    // Track campaign creation
    void recordAnalyticsEvent(AnalyticsEventTypes.CAMPAIGN_CREATED, {
      campaignId: newCampaign.id,
      campaignName: newCampaign.name
    })

    return newCampaign
  }

  const destroy = (campaign: Campaign) => {
    const storedCampaigns = config.value?.campaigns
    const foundIndex = storedCampaigns?.findIndex((c) => c.id === campaign.id) ?? -1
    if (!storedCampaigns || foundIndex === -1) {
      return
    }

    // Track campaign deletion
    void recordAnalyticsEvent(AnalyticsEventTypes.CAMPAIGN_DELETED, {
      campaignId: campaign.id,
      campaignName: campaign.name
    })

    storedCampaigns.splice(foundIndex, 1)
  }

  const duplicate = (campaign: Campaign) => {
    const newCampaign = {
      ...klona(campaign),
      id: makeId(idPrefixes.CAMPAIGN),
      name: `${campaign.name} (Copy)`
    }

    config.value?.campaigns.push(newCampaign)

    // Track campaign duplication
    void recordAnalyticsEvent(AnalyticsEventTypes.CAMPAIGN_CREATED, {
      campaignId: newCampaign.id,
      campaignName: newCampaign.name
    })

    return newCampaign
  }

  return {
    campaigns,
    getById,
    create,
    destroy,
    duplicate
  }
})
